package com.evoapps.evoshift.domain.usecase

import com.evoapps.evoshift.domain.util.AppClock
import com.evoapps.evoshift.domain.model.NotificationData
import com.evoapps.evoshift.domain.model.Notification
import com.evoapps.evoshift.domain.model.NotificationType
import com.evoapps.evoshift.domain.model.SwapRequest
import com.evoapps.evoshift.domain.model.SwapStatus
import com.evoapps.evoshift.domain.model.RosterPublication
import com.evoapps.evoshift.domain.model.RosterPublishing
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.awaitsPublishing
import kotlinx.coroutines.flow.first
import com.evoapps.evoshift.domain.repository.RosterRepository
import com.evoapps.evoshift.domain.repository.ShiftRepository
import com.evoapps.evoshift.domain.repository.UserRepository
import com.evoapps.evoshift.domain.repository.WorkplaceRepository
import com.evoapps.evoshift.domain.usecase.finance.RegenerateDailyRecordsUseCase
import com.evoapps.evoshift.domain.usecase.shift.DeleteShiftUseCase

/**
 * Publishes a roster:
 * - unassigned shifts a manager marked as open ([Shift.isOpen]) are kept, published and offered on
 *   the marketplace (a swap request with status AVAILABLE, initiated by the publisher);
 * - other unassigned shifts are skeleton leftovers and are removed;
 * - every assigned shift gets its cost snapshot (when the publisher may read rates) and is marked
 *   published;
 * - the daily records are recomputed (entered takings are kept) and the roster becomes PUBLISHED;
 * - every employee is told how many shifts they have, and the team hears about open shifts.
 *
 * SH-271: a republish (the month went to the team before) raises the roster's version and tells
 * only the people whose shifts changed since ([RosterPublishing.changedPeople]): "Roster updated",
 * and the e-mails (functions onRosterPublished) go to them alone. Only open shifts not offered yet
 * are announced again ([RosterPublishing.announcedOpen]).
 */
class PublishRosterUseCase(
    private val rosterRepository: RosterRepository,
    private val shiftRepository: ShiftRepository,
    private val userRepository: UserRepository,
    private val workplaceRepository: WorkplaceRepository,
    private val regenerateDailyRecordsUseCase: RegenerateDailyRecordsUseCase,
    private val calculateShiftCostUseCase: CalculateShiftCostUseCase,
    private val deleteShiftUseCase: DeleteShiftUseCase
) {
    suspend operator fun invoke(rosterId: String, publisherId: String): Result<Unit> = runCatching {
        val roster = rosterRepository.getRoster(rosterId).getOrThrow()
        val shifts = shiftRepository.getShiftsForRoster(rosterId).getOrThrow()
        // SH-263: published shifts deleted since the last publish (their people hear about it too).
        val removed = shiftRepository.observeRemovedShifts(rosterId).first()
        val (assigned, unassigned) = shifts.partition { it.userId != null }
        val (open, leftovers) = unassigned.partition { it.isOpen }
        // SH-271: read before the flags are cleared (null on the first publish: everyone is told).
        val republish = RosterPublishing.wasPublished(roster.status)
        val version = if (republish) roster.currentVersion + 1 else roster.currentVersion
        val changed = if (republish) RosterPublishing.changedPeople(shifts, removed) else null
        val announced = RosterPublishing.announcedOpen(republish, open)

        leftovers.forEach { deleteShiftUseCase(it.id).getOrThrow() }

        val now = AppClock.now().toString()
        val publisherName = userRepository.getUserProfile(publisherId).getOrNull()?.displayName.orEmpty()
        val openPublished = open.map { shift ->
            // Keep a claim in progress (TAKEN_PENDING) when republishing.
            val offer = shift.swapRequest ?: SwapRequest(
                status = SwapStatus.AVAILABLE,
                initiatorUserId = publisherId,
                initiatorName = publisherName,
                candidateUserId = null,
                candidateName = null,
                managerNote = null,
                createdAt = now
            )
            shift.copy(isPublished = true, hasUnpublishedChanges = false, version = version, publishedUserId = null, swapRequest = offer)
        }
        // One batched write for the whole month, not one round trip per shift.
        if (openPublished.isNotEmpty()) shiftRepository.updateShifts(openPublished).getOrThrow()

        // Cost snapshots need the employees' rates (financial managers only); an operational
        // manager publishes without them and a financial manager's refresh fills them in. Runs
        // before the flags are cleared: shifts edited since the last publish are repriced.
        calculateShiftCostUseCase.refreshRoster(rosterId)
            .onFailure { println("Warning: cost snapshots of $rosterId not refreshed: ${it.message}") }
        val assignedPublished = assigned
            .filter { it.awaitsPublishing }
            .map { it.copy(isPublished = true, hasUnpublishedChanges = false, version = version, publishedUserId = null) }
        if (assignedPublished.isNotEmpty()) shiftRepository.updateShifts(assignedPublished).getOrThrow()

        // SH-263: the removals are now what the team sees (before the roster says published, so a
        // failure leaves the month to publish again).
        shiftRepository.clearRemovedShifts(rosterId).getOrThrow()

        regenerateDailyRecordsUseCase(roster.workplaceId, rosterId, roster.year, roster.month)
            .onFailure { println("Warning: daily records of $rosterId not regenerated: ${it.message}") }
        rosterRepository.publishRoster(rosterId, RosterPublication(version, changed?.sorted())).getOrThrow()

        val workplaceName = workplaceRepository.getWorkplace(roster.workplaceId).getOrNull()?.name ?: "Your Workplace"
        // SH-263: someone whose only shifts were removed is told too (with the shifts they have left: none).
        val byPerson = assigned.groupBy { it.userId!! } +
            removed.mapNotNull { it.userId }.filter { uid -> assigned.none { it.userId == uid } }.associateWith { emptyList<Shift>() }
        // SH-271: on a republish, only whose shifts changed (also someone a shift was taken from).
        val told = if (changed == null) byPerson else changed.associateWith { uid -> byPerson[uid].orEmpty() }
        told.forEach { (userId, userShifts) ->
            userRepository.sendNotification(
                Notification(
                    userId = userId,
                    title = (if (republish) "Roster Updated: " else "Roster Published: ") + workplaceName,
                    message = "You have ${userShifts.size} shifts assigned for ${roster.getMonthYear()} in $workplaceName.",
                    type = NotificationType.ROSTER_PUBLISHED,
                    createdAt = now,
                    data = buildMap {
                        put(NotificationData.ROSTER_ID, rosterId)
                        put(NotificationData.WORKPLACE_ID, roster.workplaceId)
                        put(NotificationData.WORKPLACE_NAME, workplaceName)
                        put(NotificationData.MONTH, roster.month.toString())
                        put(NotificationData.YEAR, roster.year.toString())
                        put(NotificationData.COUNT, userShifts.size.toString())
                        if (republish) put(NotificationData.KIND, NotificationData.Kind.ROSTER_UPDATED)
                    }
                )
            )
        }

        if (announced.isNotEmpty()) {
            val team = userRepository.getAssignableEmployees(roster.workplaceId).getOrNull().orEmpty()
                .filter { it.uid != publisherId }
            team.forEach { member ->
                userRepository.sendNotification(
                    Notification(
                        userId = member.uid,
                        title = "Open shifts: $workplaceName",
                        message = "${announced.size} open shifts in ${roster.getMonthYear()} are waiting to be taken.",
                        type = NotificationType.OPEN_SHIFTS_AVAILABLE,
                        createdAt = now,
                        data = mapOf(
                            NotificationData.ROSTER_ID to rosterId,
                            NotificationData.WORKPLACE_ID to roster.workplaceId,
                            NotificationData.WORKPLACE_NAME to workplaceName,
                            NotificationData.MONTH to roster.month.toString(),
                            NotificationData.YEAR to roster.year.toString(),
                            NotificationData.COUNT to announced.size.toString()
                        )
                    )
                )
            }
        }
    }
}
